/**
 * The monthly supplier scorecard email setting (CR-7067).
 *
 * Its own hook rather than more state on `useNotificationSetup`, for the same
 * reason as {@link useSendBudgets}: this is a different resource on a different
 * endpoint (`/core/settings/performance/`, not the notification settings row),
 * and the setup hook is already at the file-size ceiling.
 *
 * The two fields live on `SupplierPerformanceSettings` because that model is a
 * OneToOne on the company, so the flag is genuinely per-customer. They are
 * surfaced on Notification Setup rather than on the performance screen because
 * this is a question about *sending mail*, which is what an admin comes to this
 * page to answer.
 *
 * Writes apply immediately, matching the master switches, quiet hours and the
 * send budget on the same page — a toggle and a day are not worth a second
 * staged/save state for the unsaved-changes guard to track.
 *
 * @link ./SupplierScorecardCard for the screen.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { performanceSettingsAPI } from '../../../../lib/api';
import { getErrorMessage } from '../../../../hooks/useFetchData';

export interface UseScorecardEmailResult {
  loading: boolean;
  error: string | null;
  saving: boolean;
  /** Whether suppliers are emailed their scorecard at all. */
  enabled: boolean;
  /** Day of the month the send runs. 1-28. */
  day: number;
  reload: () => Promise<void>;
  save: (patch: { enabled?: boolean; day?: number }) => Promise<void>;
}

/** The backend caps the day at 28 so it exists in February too. */
export const MAX_SEND_DAY = 28;

export function useScorecardEmail(): UseScorecardEmailResult {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [day, setDay] = useState(5);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await performanceSettingsAPI.getSettings();
      setEnabled(Boolean(data.scorecard_email_enabled));
      setDay(data.scorecard_email_day ?? 5);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const save = useCallback(
    async (patch: { enabled?: boolean; day?: number }) => {
      // Optimistic, then rolled back on failure: the control is a toggle, and
      // one that snaps back is clearer than one that waits.
      const previous = { enabled, day };
      if (patch.enabled !== undefined) setEnabled(patch.enabled);
      if (patch.day !== undefined) setDay(patch.day);

      setSaving(true);
      try {
        await performanceSettingsAPI.updateSettings({
          ...(patch.enabled !== undefined && { scorecard_email_enabled: patch.enabled }),
          ...(patch.day !== undefined && { scorecard_email_day: patch.day }),
        });
        toast.success('Scorecard email settings saved');
      } catch (err: unknown) {
        setEnabled(previous.enabled);
        setDay(previous.day);
        toast.error(getErrorMessage(err));
      } finally {
        setSaving(false);
      }
    },
    [enabled, day],
  );

  return { loading, error, saving, enabled, day, reload, save };
}
